import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import toast from 'react-hot-toast';

import { Card, CardHeader, ContentLoader } from '@/components/common';
import { PageHeader } from '@/components/layout';
import { getErrorMessage } from '@/services/api';
import { insurerReportsApi } from '@/services/insurerReports';
import { formatCurrency } from '@/utils';

const CELL = 'py-2 pr-4 text-sm text-gray-900 dark:text-gray-100';
const HEAD = 'py-2 pr-4 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400';

function Section({
  title,
  rows,
}: {
  title: string;
  rows: Array<{ key: string; name: string; policy_count: number; premium: string }>;
}) {
  return (
    <Card>
      <CardHeader title={title} />
      {rows.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">No policies in this period.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr>
                <th className={HEAD}>Name</th>
                <th className={`${HEAD} text-right`}>Policies</th>
                <th className={`${HEAD} text-right`}>Premium</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} className="border-t border-gray-100 dark:border-gray-700">
                  <td className={CELL}>{r.name}</td>
                  <td className={`${CELL} text-right`}>{r.policy_count}</td>
                  <td className={`${CELL} text-right`}>{formatCurrency(r.premium)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

/** Insurer business report (board #362): your own issued business only. */
export function ReportsPage() {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const filters = { date_from: from, date_to: to };
  const query = useQuery({
    queryKey: ['insurer-business-report', from, to],
    queryFn: () => insurerReportsApi.business(filters),
  });
  const data = query.data;

  const download = async () => {
    try {
      const blob = await insurerReportsApi.businessCsv(filters);
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = 'insurer-business-report.csv';
      a.click();
      URL.revokeObjectURL(href);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Business reports" subtitle="Premium and policy counts on your own paper" />
      <Card>
        <div className="flex flex-wrap items-end gap-4">
          <label className="text-sm text-gray-700 dark:text-gray-300">
            From
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="mt-1 block rounded border border-gray-300 bg-white px-2 py-1 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
            />
          </label>
          <label className="text-sm text-gray-700 dark:text-gray-300">
            To
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="mt-1 block rounded border border-gray-300 bg-white px-2 py-1 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
            />
          </label>
          <button
            type="button"
            onClick={download}
            className="rounded bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700"
          >
            Download CSV
          </button>
        </div>
      </Card>

      {query.isLoading ? (
        <ContentLoader />
      ) : query.isError || !data ? (
        <div className="text-sm text-red-600 dark:text-red-400">
          Could not load the report.{' '}
          <button type="button" className="underline" onClick={() => query.refetch()}>
            Try again
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Card>
              <p className="text-sm text-gray-500 dark:text-gray-400">Gross written premium</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {formatCurrency(data.totals.gross_written_premium)}
              </p>
            </Card>
            <Card>
              <p className="text-sm text-gray-500 dark:text-gray-400">Policy count</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{data.totals.policy_count}</p>
            </Card>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Issued policies on your paper, cancelled policies left out ({data.totals.cancelled_policies} cancelled in
            this period). Month is the issue date.
          </p>
          <Section
            title="Broker-wise business"
            rows={data.by_broker.map((r) => ({ key: r.broker_id, name: r.broker_name, ...r }))}
          />
          <Section
            title="Product-wise business"
            rows={data.by_product.map((r) => ({ key: r.product_id ?? r.product_name, name: r.product_name, ...r }))}
          />
          <Section title="Monthly business" rows={data.by_month.map((r) => ({ key: r.month, name: r.month, ...r }))} />
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Claims and commission reports will follow with the claims desk and commission schedules.
          </p>
        </>
      )}
    </div>
  );
}
